import { agreementVariablesList } from "components/ContractsSettings/ContractEditorLexical/variables";
import type { UserVariables } from "components/shared/AgreementModal";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import type { MissingVariable } from "containers/ContractValidationEdit/utils";
import { SmallBodyHighlight } from "helpers/TypographyComponents";
import type { SetStateAction } from "react";
import type { ChannelCompensationGroup } from "store/channelStore";

interface AgreementVariablesListProps {
    variables: FoundVariable[] | FoundBeneficiaryVariable[];
    // onVariableChange?: (variables?: UserVariables) => void;
    onVariableBlur?: (variables?: UserVariables) => void;
    prefillWith?: UserVariables;
    /**
     * Verify if user is the creator of the agreement
     */
    isOwner: boolean;
    /**
     * Found user variables
     */
    userVariables: UserVariables | undefined;
    /**
     * Set user variables state
     */
    setUserVariables: React.Dispatch<SetStateAction<UserVariables | undefined>>;
    /**
     * If this component is used for multi beneficiaries panels
     */
    hasMultiBeneficiaries?: boolean;
    /**
     * Channel compensation groups array
     */
    channelCompensationGroups?: ChannelCompensationGroup[];
    /**
     * Missing variables array
     */
    missingVariables: MissingVariable[];
    /**
     * Missing variables for beneficiaries array
     */
    beneficiariesMissingVariables: MissingVariable[];
    /**
     * Skip the scroll wrapper when the parent already scrolls grouped lists
     */
    embedded?: boolean;
}

export type FoundVariable = { variable: string; name: string; index?: number };
export type FoundBeneficiaryVariable = { variable: string; name: string; multiBeneficiaryKey: string };

/**
 * The component that renders the list of variables to be filled in the contract
 * It can't take directly an UserVariables object because it doen't have access to the editor, so you need to find them in the parent
 * @param variables FoundVariable[] List of found with regex variables
 * @param onVariableChange (variables?: UserVariables) => void Callback function to handle variable change
 * @param onVariableBlur (variables?: UserVariables) => void Callback function to handle variable change using onBlur instead of onChange
 * @returns
 */
export const AgreementVariablesList = ({
    variables,
    onVariableBlur,
    isOwner,
    userVariables,
    setUserVariables,
    hasMultiBeneficiaries = false,
    missingVariables,
    beneficiariesMissingVariables,
    embedded = false,
}: AgreementVariablesListProps) => {
    const { variablesLabels } = agreementVariablesList;

    const onChangeVariable = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUserVariables((prev) => {
            const next = {
                ...prev,
                [e.target.name]: e.target.value,
            };
            return next;
        });
    };

    const handleVariableBlur = (_e: any, variable: string, multiBenefiaryKey: string) => {
        if (onVariableBlur) onVariableBlur(userVariables);
        const editor = document.querySelector(".ContentEditable-wrapper__agreementEdit");
        if (editor) {
            const variableElement = multiBenefiaryKey
                ? editor.querySelector(`[multi-beneficiary-variable-key="${multiBenefiaryKey}"]`)
                : editor.querySelector(`[data-variable-name="${variable}"]`);
            setTimeout(() => {
                if (variableElement) variableElement.classList.remove("focused");
            }, 700);
        }
    };

    const handlePressEnter = (e: any) => {
        if (e.keyCode === 13) {
            e.target.blur();
        }
    };

    // Logic on click variable title, scroll to variable
    const handleScrollToVariable = (variable: string, multiBeneficiaryKey: string) => {
        const editor = document.querySelector(".ContentEditable-wrapper__agreementEdit");
        if (editor) {
            const variableElement = multiBeneficiaryKey
                ? editor.querySelector(`[multi-beneficiary-variable-key="${multiBeneficiaryKey}"]`)
                : editor.querySelector(`[data-variable-name="${variable}"]:not([multi-beneficiary-variable-key])`) ||
                  editor.querySelector(`[data-variable-name="${variable}"]`);
            if (variableElement) {
                variableElement.scrollIntoView({ behavior: "smooth", block: "center" });
                variableElement.classList.add("focused");
            }
        }
    };
    /**
     * Render required props fields. Highlight field with error
     * @param variableKey variable key
     */
    const requiredFieldProps = (variableKey: string) => {
        const showError = [...missingVariables, ...beneficiariesMissingVariables]?.some(
            (variable) => variable.key === variableKey,
        );
        if (showError) {
            return { error: true };
        }
    };

    const list = (
        <div className="agreement-step-2-variables">
            {variables.map((variable, i) => {
                const { variable: variableName } = variable;
                const label = variablesLabels[variableName] ?? variableName; // fallback for custom variables {{variable custom}}

                return (
                    <div className="agreement-step-2-variables-field" key={i}>
                        <div className="agreement-step-2-variables-label">
                            <SmallBodyHighlight color="grey" className="agreement-variable-title">
                                {label}
                            </SmallBodyHighlight>
                        </div>
                        <StyledTextField
                            {...requiredFieldProps(variable?.multiBeneficiaryKey ?? variable.name)}
                            value={
                                hasMultiBeneficiaries
                                    ? userVariables?.[variable.multiBeneficiaryKey]
                                    : userVariables?.[variable.name]
                            }
                            onChange={onChangeVariable}
                            onBlur={(e) => handleVariableBlur(e, variable.variable, variable.multiBeneficiaryKey)}
                            name={hasMultiBeneficiaries ? variable.multiBeneficiaryKey : variable.name}
                            size="small"
                            onClick={() => handleScrollToVariable(variable.variable, variable.multiBeneficiaryKey)}
                            disabled={!isOwner}
                            onKeyDown={handlePressEnter}
                            fullWidth
                        />
                    </div>
                );
            })}
        </div>
    );

    if (embedded) return list;

    return <ScrollShadowWrapper className="agreement-step-2-variables-wrapper">{list}</ScrollShadowWrapper>;
};
